import { Observable } from 'rxjs/Rx'
import { Course } from '../../models/course'
import { HttpHelper } from '../../../helpers'
import { CourseService } from '../course/course.service'
import { CartItemAssociationService } from '../cart-item-association'
import { CartItemAssociation } from '../../models/cart-item-association'
import { ProductRecommendation } from '../../models/product-recommendation'
import { KtpEnrollmentRecommendationService } from '..'

// MP-TODO: (enhancement): Service Under Development |
// This service is temporary in its current state and the implementation should
// change once we create proper product upgrade API
export class ProductRecommendationService {
  private _legacyItemsEndpoint: string = '//apis.manhattanprep.com/legacy/:testLine/normalizeditems/:action?:params'
  private _cartBaseUrl: string = '//www.manhattanprep.com/:testline/cart/'
  private _courseFinderUrl: string = '//www.manhattanprep.com/:testline/classes/'

  public constructor(
    private _httpHelper: HttpHelper,
    private _courseService: CourseService,
    private _cartItemAssociationService: CartItemAssociationService,
    private _ktpEnrollmentRecommendationService: KtpEnrollmentRecommendationService
  ) {}

  public findUserUpgrades(userId: string): Observable<ProductRecommendation[]> {
    return Observable.zip(
      this._getCourseRecommendations(userId),
      this._getItemRecommendations(userId),
      this._getKtpEnrollmentRecommendations()
    ).map((productRecommendationLists) => {
      const cleanedLists = productRecommendationLists.map((recommendations: ProductRecommendation[]) => {
        return (recommendations || []).filter((recommendation: ProductRecommendation) => recommendation)
      })
      return [].concat(...cleanedLists)
    })
  }

  private _getCourseRecommendations(userId): Observable<ProductRecommendation[]> {
    return this._courseService.findUserUpgrades(userId)
      .map((upgrades: Course[]) => {
        return upgrades.map((course) => this._getLegacyProductRecommendationFromCourse(course))
      })
  }

  private _getKtpEnrollmentRecommendations(): Observable<ProductRecommendation[]> {
    return this._ktpEnrollmentRecommendationService.get()
      .concatMap((enrollments) => {
        const courseRecommendations: Observable<ProductRecommendation[]> = enrollments.courses.map((courses) => {
          return courses.map((course) => this._getLegacyProductRecommendationFromCourse(course))
        })
        const itemRecommendations: Observable<ProductRecommendation[]> = enrollments.items.map((items) => {
          return items.map((item) => {
            return this._createProductRecommendation(
              item.name,
              '', 'item', 'upgrade', item.price, item.cartUrl,
              item.testline
            )
          })
        })
        return Observable.zip(courseRecommendations, itemRecommendations)
          .map((data) => [].concat(...data))
      })
  }

  private _getLegacyProductRecommendationFromCourse(course: Course): ProductRecommendation {
    const testLine = course.testLine
    course.cartUrl = course.cartUrl || this._getCartUrl(course)

    return this._createProductRecommendation(
      course.name, '', 'event', 'upgrade', course.price, course.cartUrl,
      testLine, course.deactivateOn, course.active, {
        instructor: course.instructor,
        coInstructor: course.coInstructor,
        startDate: course.startDate,
        endDate: course.endDate,
        location: course.location,
        schedule: course.schedule,
        active: course.active
      }
    )
  }

  private _getItemRecommendations(_userId): Observable<ProductRecommendation[]> {
    return this._cartItemAssociationService.findAllModels([
      'purchasedProduct.testLine', 'associatedCartItem'
    ]).concatMap((associations: CartItemAssociation[]) => {
      const recommendations = associations.map(
        (assoc: CartItemAssociation) => this._getLegacyProductRecommendationFromAssociation(assoc)
      )
      const recommendationObservable = recommendations.length > 0 ?
        Observable.zip(...recommendations) :
        Observable.of(undefined)

      return recommendationObservable
    })
  }

  private _getLegacyProductRecommendationFromAssociation(assoc: CartItemAssociation): Observable<ProductRecommendation> {
    if (!assoc.associatedCartItem || !assoc.purchasedProduct ||
        !assoc.purchasedProduct.testLine || !this._isLegacyHandle(assoc.associatedCartItem.handle)) {
      return Observable.of(undefined)
    }

    return this._getAssociatedItemPrice([assoc]).map((itemInfo) => {
      const testLine = assoc.purchasedProduct.testLine.name
      const itemId = this._getItemIdFromLegacyHandle(assoc.associatedCartItem.handle)
      const cartUrl = this._getUrlForCart(testLine, itemId, '1')

      return this._createProductRecommendation(
        assoc.associatedCartItem.name,
        '', 'item', 'upgrade', itemInfo[0], cartUrl,
        testLine
      )
    })
  }

  private _isLegacyHandle(cartItemHandle: string): boolean {
    return String(cartItemHandle || '').startsWith('legacy/')
  }

  private _getAssociatedItemPrice(associations: CartItemAssociation[]): Observable<string[]> {
    const observables = associations.map((cartItemAssociation: CartItemAssociation) => {
      const product = cartItemAssociation.purchasedProduct
      const cartItem = cartItemAssociation.associatedCartItem

      if (!product || !product.testLine || !cartItem) {
        return Observable.of(undefined)
      }

      return this._httpHelper.get(
        this._getLegacyItemsEndpoint(product.testLine.name, 'show', `id=${cartItem.handle}`)
      ).map((result) => result['data']['price'] as string)
    })
    return Observable.zip(...observables)
  }

  private _getLegacyItemsEndpoint(testLine: string, action: string, params: string): string {
    return this._legacyItemsEndpoint.replace(':testLine', testLine)
      .replace(':action', action)
      .replace(':params', params)
  }

  private _getItemIdFromLegacyHandle(cartItemHandle: string): string {
    const itemIdMatches = /.*\/item\/(\d+)/.exec(cartItemHandle)
    return itemIdMatches[1]
  }

  private _getCartUrl(course) {
    return course.active ? this._getUrlForCart(course.testLine, course.id, '0') : this._getUrlForItemSearch(course.testLine)
  }

  private _getUrlForCart(testLine: string, itemId: string, itemType: string): string {
    return `${this._cartBaseUrl}?itemID=${itemId}&ItemType=${itemType}&act=Add`
      .replace(':testline', testLine)
  }

  private _getUrlForItemSearch(testLine: string): string {
    return this._courseFinderUrl.replace(':testline', testLine)
  }

  private _createProductRecommendation(
    name: string,
    description: string,
    recommendedItemType: 'event'|'item',
    recommendationReason: 'upgrade'|'extension'|'recommendation',
    price: string,
    url: string,
    testLine: string,
    lastPurchaseDate?: Date,
    available: boolean = true,
    metaData: {[key: string]: string|Date|number|boolean} = {}
  ): ProductRecommendation {
    const r = new ProductRecommendation()
    r.name = name
    r.description = description
    r.price = price
    r.cartUrl = url
    r.lastPurchaseDate = lastPurchaseDate
    r.testLine = testLine

    r.recommendedItemType = recommendedItemType
    r.recommendationReason = recommendationReason
    r.available = available
    r.metaData = metaData
    return r
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/data/services/product-recommendation/product-recommendation.service.ts